/**
 * JoinTeam — public page used by players to self-register through the coach's
 * universal join link (`/join/:slug`). No auth required to view the page.
 *
 * Flow:
 *   1. Load coach + teams via GET /api/join/:slug
 *   2. Player picks team, enters name + password (+ PIN if required by team)
 *   3. POST /api/join/:slug → token + user → store + navigate to /dashboard
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, setAuthHeader } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LangContext';
import { toast } from 'sonner';
import { Shield, Lock, User as UserIcon, ChevronRight, Users } from 'lucide-react';

export default function JoinTeam() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { t } = useLang();

  const [coach, setCoach] = useState(null);
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const selectedTeam = useMemo(() => teams.find(t => t.id === teamId), [teams, teamId]);
  const pinRequired = !!selectedTeam?.requires_pin;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/join/${slug}`);
        if (cancelled) return;
        setCoach({ name: r.data.coach_name, id: r.data.coach_id });
        setTeams(r.data.teams || []);
        if (r.data.teams?.length === 1) setTeamId(r.data.teams[0].id);
      } catch (e) {
        if (cancelled) return;
        setLoadError(e?.response?.data?.detail || 'No se pudo cargar el link');
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const valid = teamId && name.trim().length >= 2 && password.length >= 6 && (!pinRequired || pin.length >= 4);

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/join/${slug}`, {
        team_id: teamId,
        name: name.trim(),
        password,
        pin: pinRequired ? pin.trim() : undefined,
      });
      setAuthHeader(data.token);
      setUser?.(data.user);
      toast.success(t('register_success') || `¡Bienvenido, ${data.user.name}!`);
      // Players land directly on the playbook (their primary surface) — the
      // /dashboard hop would just trigger an extra redirect via Protected.
      navigate(data.user.role === 'player' ? '/playbook' : '/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo registrar');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <main className="min-h-screen bg-navy-deep text-white flex items-center justify-center p-6">
        <div className="bg-card-steel border border-red-500/40 rounded-lg p-6 max-w-md text-center" data-testid="join-error">
          <Shield className="w-10 h-10 text-red-400 mx-auto mb-2" />
          <h1 className="font-h uppercase text-xl text-white mb-2">Link no disponible</h1>
          <p className="text-sm text-gray-300">{loadError}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-navy-deep text-white px-4 py-8 flex items-start justify-center" data-testid="join-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-gold/80 mb-2">PlayMaster IA</div>
          <h1 className="font-h uppercase text-3xl text-white">Únete al equipo</h1>
          <p className="text-sm text-gray-400 mt-1">
            Invitación de <span className="text-gold font-semibold">{coach?.name || '…'}</span>
          </p>
        </div>

        <form onSubmit={submit} className="bg-card-steel border border-gold/30 rounded-lg p-5 space-y-4" data-testid="join-form">
          {/* Team picker */}
          <div>
            <label className="block text-[11px] font-mono uppercase tracking-widest text-gold/80 mb-2">
              <Users className="w-3.5 h-3.5 inline me-1" /> Elegí tu equipo
            </label>
            <div className="space-y-2" data-testid="join-team-list">
              {teams.map(tm => (
                <button
                  key={tm.id}
                  type="button"
                  onClick={() => setTeamId(tm.id)}
                  data-testid={`join-team-${tm.id}`}
                  className={`w-full flex items-center gap-3 p-3 rounded-md border-2 transition text-left ${
                    teamId === tm.id ? 'border-gold bg-gold/10' : 'border-steel hover:border-gold/40'
                  }`}
                >
                  {tm.logo_url ? (
                    <img src={tm.logo_url} alt={tm.name} className="w-10 h-10 rounded-full object-cover border border-gold/30 flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center flex-shrink-0">
                      <Shield className="w-5 h-5 text-gold" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-h uppercase text-sm tracking-wider text-white">{tm.name}</div>
                    <div className="text-[10px] text-gray-400 mt-0.5">
                      {tm.category === 'male' ? 'Masculino' : tm.category === 'female' ? 'Femenino' : 'Mixto'}
                      {tm.requires_pin && <span className="ms-2 text-gold/80"><Lock className="w-3 h-3 inline" /> PIN</span>}
                    </div>
                  </div>
                  {teamId === tm.id && <ChevronRight className="w-4 h-4 text-gold" />}
                </button>
              ))}
              {teams.length === 0 && (
                <div className="text-sm text-gray-400 text-center py-4">
                  Este coach todavía no tiene equipos creados.
                </div>
              )}
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="block text-[11px] font-mono uppercase tracking-widest text-gold/80 mb-1">
              <UserIcon className="w-3.5 h-3.5 inline me-1" /> Tu nombre
            </label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              data-testid="join-name"
              maxLength={80}
              autoComplete="given-name"
              placeholder="Ej. Carlos Pérez"
              className="w-full bg-navy-deep border border-steel focus:border-gold rounded px-3 py-2.5 text-sm text-white outline-none"
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-[11px] font-mono uppercase tracking-widest text-gold/80 mb-1">
              <Lock className="w-3.5 h-3.5 inline me-1" /> Clave (mínimo 6 caracteres)
            </label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              data-testid="join-password"
              minLength={6}
              maxLength={120}
              autoComplete="new-password"
              className="w-full bg-navy-deep border border-steel focus:border-gold rounded px-3 py-2.5 text-sm text-white outline-none"
            />
          </div>

          {/* PIN (only if team requires it) */}
          {pinRequired && (
            <div data-testid="join-pin-wrap">
              <label className="block text-[11px] font-mono uppercase tracking-widest text-gold/80 mb-1">
                PIN del equipo (pedíselo a tu coach)
              </label>
              <input
                inputMode="numeric"
                pattern="[0-9]*"
                value={pin}
                onChange={e => setPin(e.target.value.replace(/[^0-9]/g, '').slice(0, 8))}
                data-testid="join-pin"
                maxLength={8}
                placeholder="4-8 dígitos"
                className="w-full bg-navy-deep border border-steel focus:border-gold rounded px-3 py-2.5 text-sm text-white outline-none tracking-[0.4em] font-mono"
              />
            </div>
          )}

          <button
            type="submit"
            disabled={!valid || submitting}
            data-testid="join-submit"
            className="w-full mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-gold text-navy font-h uppercase tracking-wider font-bold hover:bg-gold-light disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? '...' : (<>Entrar al equipo <ChevronRight className="w-4 h-4" /></>)}
          </button>

          <p className="text-[11px] text-gray-500 text-center mt-3">
            Al unirte aceptás que tu coach vea tu nombre y datos del Roster.
          </p>
        </form>
      </div>
    </main>
  );
}
